import Highcharts from 'highcharts';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import highchartsPatternFill from 'highcharts/modules/pattern-fill';
import {
  format,
} from 'date-fns';
import {
  includes,
  map,
  toString,
} from 'lodash';

import { truncationValues } from '../../../../Visualization/visualizationConstants';
import { Timescale } from '../../../../../types/models';
import { IChartData } from '../index';

// Initialize Highcharts modules
export const initializeHighcharts = () => {
  highchartsAccessibility(Highcharts);
  highchartsPatternFill(Highcharts);
  
  // Global configuration
  window.Buffer = window.Buffer || require('buffer').Buffer;
  Highcharts.AST.allowedTags.push('Box');
  Highcharts.AST.allowedAttributes.push('data-html2canvas-ignore');
};

// Chart color palette
export const colors = [
  '#27aae1',
  '#9c27b0',
  '#e91e63',
  '#43a047',
  '#0d47a1',
  '#ba68c8',
  '#ff9800',
  '#00bfa5',
  '#304ffe',
  '#613aa5',
  '#ffcea3',
  '#cddc39',
  '#607d8b',
  '#cc79a7',
  '#795548',
];

// Constants for styling
export const CHART_HEIGHT = '500px';
export const FONT_SIZE = '13px';
export const FONT_FAMILY = 'Roboto, Helvetica, Arial, sans-serif';

// Helper function to format numbers
export const formatNumber = (
  value: number,
  formatType: string,
  prefix: string,
  suffix: string,
  truncation: number,
  decimalPlaces: number
): string => {
  const formattedValue = Highcharts.numberFormat(
    (formatType === 'Percent' ? value * 100 : value) / truncationValues[toString(truncation)],
    decimalPlaces,
    '.'
  )
    .toString()
    .replaceAll(' ', '')
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return `${prefix}${formattedValue}${suffix}`;
};

// Helper function to create chart configuration
export const createChartConfig = () => ({
  zooming: {
    type: 'x' as const,
    resetButton: {
      position: {
        align: 'left' as const,
        verticalAlign: 'top' as const,
        x: -10,
        y: 10,
      },
    },
  },
  type: 'column' as const,
  height: CHART_HEIGHT,
});

// Helper function to create Y-axis configuration
export const createYAxisConfig = (
  yAxisTitle: string,
  formatType: string,
  prefix: string,
  suffix: string,
  truncation: number,
  decimalPlaces: number
) => ({
  title: {
    useHTML: true,
    text: `<div style="font-size: ${FONT_SIZE}">${yAxisTitle}</div>`,
  },
  labels: {
    formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
      return formatNumber(
        this.value as number,
        formatType,
        prefix,
        suffix,
        truncation,
        decimalPlaces
      );
    },
    style: {
      fontSize: FONT_SIZE,
    },
  },
});

// Helper function to create X-axis configuration
export const createXAxisConfig = (
  data: IChartData,
  timeScaleType: Timescale,
  dateFormat: string
) => ({
  categories: map(data.Dates, (item) =>
    includes([Timescale.Weekly, Timescale.Monthly], timeScaleType)
      ? format(new Date(item.Date), dateFormat)
      : item.Date
  ),
  labels: {
    style: {
      fontSize: FONT_SIZE,
    },
  },
});

// Helper function to create dash patterns for charts
export const createDashPattern = (dashStyle: string, color: string) => {
  switch (dashStyle) {
    case 'ShortDash':
      return {
        color: {
          pattern: {
            color: color,
            path: {
              // Create solid top and bottom with gap in middle
              d: 'M 0 0 L 20 0 L 20 7 L 0 7 Z M 0 13 L 20 13 L 20 20 L 0 20 Z',
              fill: color,
            },
            width: 20,
            height: 20,
            opacity: 1,
          },
        },
      };
    case 'ShortDot':
      return {
        color: {
          pattern: {
            color: color,
            path: {
              // Create dotted pattern using horizontal lines with gaps
              d: 'M 0 1 L 8 1 M 0 4 L 8 4 M 0 7 L 8 7 M 0 10 L 8 10 M 0 13 L 8 13 M 0 16 L 8 16',
              strokeWidth: 2,
            },
            width: 8,
            height: 18,
            opacity: 1,
          },
        },
      };
    default:
      return { color };
  }
};

// Helper function to create preview data pattern
export const createPreviewPattern = (color: string) => ({
  color: {
    pattern: {
      color: color,
      path: {
        d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
        strokeWidth: 4,
      },
      width: 10,
      height: 10,
      opacity: 0.5,
    },
  },
});